import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useState, useEffect } from "react";
import SalesAuditWizardMaster, {
  type AuditFormData,
  type DerivedAuditProfile,
} from "./SalesAuditWizardMaster";
import { useAuditStore } from "@/hooks/useAuditStore";
import { ROUTE_PATHS } from "@/lib";
import { calculateAuditResult } from "@/lib/calculate-result";
import { supabase } from "@/integrations/supabase/client";

/**
 * WRAPPER FOR SALES AUDIT WIZARD MASTER
 * -------------------------------------------------------
 * Connects the standalone wizard to the existing audit store
 * and handles navigation after submission
 * INCLUDES STATE PERSISTENCE to prevent data loss
 */

const STORAGE_KEY = "salesforge_wizard_state";

export default function SalesAuditWizardMasterWrapper() {
  const navigate = useNavigate();
  const { setCompanyProfile, setAnswers, setDynamicSignals, calculateResult } = useAuditStore();

  // Load initial data from localStorage
  const [initialData, setInitialData] = useState<Partial<AuditFormData> | undefined>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : undefined;
    } catch {
      return undefined;
    }
  });

  // Save to localStorage on change
  function handleChange(payload: { raw: AuditFormData; normalized: DerivedAuditProfile }) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload.raw));
    } catch (e) {
      console.error("Failed to save wizard state:", e);
    }
  }

  async function handleSubmit(payload: { raw: AuditFormData; normalized: DerivedAuditProfile }) {
    const { raw, normalized } = payload;

    // Submit wizard data

    // Clear localStorage after successful submit
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error("Failed to clear wizard state:", e);
    }

    // Map to existing store structure
    setCompanyProfile({
      companyName: normalized.companyContext.companyName,
      companyDomain: raw.companyDomain || undefined,
      industry: normalized.companyContext.industry,
      businessModel: mapBusinessModel(normalized.companyContext.businessModel),
      revenue: 0,
      employees: "",
      // teamSize wird aus productiveReps (Step 5) gespeist – keine separate Abfrage mehr
      teamSize: raw.productiveReps ?? null,
      averageDealValue: "",
      salesCycle: "",
      hasCRM: raw.crmAvailable === "yes",
      crmName: raw.crmName || undefined,
      hasMarketingAutomation: raw.marketingAutomation === "yes",
      leadSources: [],
      acquisitionMethods: (() => {
        const rawMix: Record<string, number> = {
          phone: raw.acquisitionPhone || 0,
          email: raw.acquisitionEmail || 0,
          linkedin: raw.acquisitionLinkedIn || 0,
          xing: raw.acquisitionXing || 0,
          referrals: raw.acquisitionReferrals || 0,
          events: raw.acquisitionEvents || 0,
          contentMarketing: raw.acquisitionContentMarketing || 0,
          paidAds: raw.acquisitionPaidAds || 0,
          coldVisits: raw.acquisitionColdVisits || 0,
          partner: raw.acquisitionPartner || 0,
          other: raw.acquisitionOther || 0,
        };
        // Nur Kanäle mit Wert > 0 zurückgeben — wenn Summe 0, leeres Objekt
        const filtered = Object.fromEntries(
          Object.entries(rawMix).filter(([, v]) => v > 0)
        );
        return Object.keys(filtered).length > 0 ? filtered : {};
      })(),
      contactFirstName: raw.contactFirstName,
      contactLastName: raw.contactLastName,
      contactPosition: raw.contactRole,
      contactEmail: raw.contactEmail,
      contactPhone: raw.contactPhone || undefined,
      funnelData: {
        // CRITICAL: Store RAW values (null if not known), NOT normalized values
        averageDealSize: raw.averageDealSize ?? null,
        salesCycleMonths: raw.salesCycleMonths ?? null,
        monthlyRevenue: (raw.monthlyDeals ?? 0) * (raw.averageDealSize ?? 0) || null,
        monthlyDeals: raw.monthlyDeals ?? null,
        monthlyContacts: raw.monthlyContacts ?? null,
        monthlyDiscoveryCalls: raw.monthlyDiscoveryCalls ?? null,
        monthlyPresentations: raw.monthlyPresentations ?? null,
        responseTime: raw.responseTime || null,
        followUpDiscipline: raw.followUpDiscipline || null,
        leadQuality: raw.leadQuality || null,
        pipelineTransparency: raw.pipelineTransparency || null,
      },
    });

    // Store ALL diagnostic inputs in NEW FORMAT (rawAnswers with *Exact and *Unknown fields)
    const rawAnswersData = {
      // Store the COMPLETE raw object
      ...raw,

      // CRITICAL: Map to new architecture format with *Exact and *Unknown fields
      // NOTE: null means "not known", 0 is a VALID value (means "we know it's zero")

      // Funnel Metrics
      monthlyContactsExact: raw.monthlyContacts,
      monthlyContactsUnknown: raw.monthlyContacts === null,
      
      monthlyDiscoveryCallsExact: raw.monthlyDiscoveryCalls,
      monthlyDiscoveryCallsUnknown: raw.monthlyDiscoveryCalls === null,
      
      monthlyPresentationsExact: raw.monthlyPresentations,
      monthlyPresentationsUnknown: raw.monthlyPresentations === null,
      
      monthlyDealsExact: raw.monthlyDeals,
      monthlyDealsUnknown: raw.monthlyDeals === null,
      
      averageDealSizeExact: raw.averageDealSize,
      averageDealSizeUnknown: raw.averageDealSize === null,
      
      salesCycleExact: raw.salesCycleMonths,
      salesCycleUnknown: raw.salesCycleMonths === null,
      
      // Conversion Rates (Step 1)
      // Priority: exact input > range midpoint > null (fallback in engine)
      // Branchen-Benchmarks als Fallback bei vollständig unbekannten Conversion-Rates
      // Leads → Kontakt: 30%, Kontakt → Meeting: 25%, Meeting → Abschluss: 25%
      convLeadToDiscoveryExact: (() => {
        if (raw.conversionLeadToDiscovery !== null) return raw.conversionLeadToDiscovery;
        const fromRange = convRangeToMidpoint(raw.conversionLeadToDiscoveryRange);
        if (fromRange !== null) return fromRange;
        if (raw.convLeadToDiscoveryUnknown) return 30; // Branchenschnitt-Fallback
        return null;
      })(),
      convLeadToDiscoveryUnknown:
        raw.conversionLeadToDiscovery === null &&
        (!raw.conversionLeadToDiscoveryRange || raw.conversionLeadToDiscoveryRange === "not-known"),
      // Badge-Flag für UI: ob der Branchenschnitt-Fallback aktiv ist
      convLeadToDiscoveryUsesBenchmark:
        raw.convLeadToDiscoveryUnknown === true &&
        raw.conversionLeadToDiscovery === null &&
        (!raw.conversionLeadToDiscoveryRange || raw.conversionLeadToDiscoveryRange === "not-known"),

      convDiscoveryToOfferExact: (() => {
        if (raw.conversionDiscoveryToOffer !== null) return raw.conversionDiscoveryToOffer;
        const fromRange = convRangeToMidpoint(raw.conversionDiscoveryToOfferRange);
        if (fromRange !== null) return fromRange;
        if (raw.convDiscoveryToOfferUnknown) return 25; // Branchenschnitt-Fallback
        return null;
      })(),
      convDiscoveryToOfferUnknown:
        raw.conversionDiscoveryToOffer === null &&
        (!raw.conversionDiscoveryToOfferRange || raw.conversionDiscoveryToOfferRange === "not-known"),
      convDiscoveryToOfferUsesBenchmark:
        raw.convDiscoveryToOfferUnknown === true &&
        raw.conversionDiscoveryToOffer === null &&
        (!raw.conversionDiscoveryToOfferRange || raw.conversionDiscoveryToOfferRange === "not-known"),

      convOfferToCloseExact: (() => {
        if (raw.conversionOfferToClose !== null) return raw.conversionOfferToClose;
        const fromRange = convRangeToMidpoint(raw.conversionOfferToCloseRange);
        if (fromRange !== null) return fromRange;
        if (raw.convOfferToCloseUnknown) return 25; // Branchenschnitt-Fallback
        return null;
      })(),
      convOfferToCloseUnknown:
        raw.conversionOfferToClose === null &&
        (!raw.conversionOfferToCloseRange || raw.conversionOfferToCloseRange === "not-known"),
      convOfferToCloseUsesBenchmark:
        raw.convOfferToCloseUnknown === true &&
        raw.conversionOfferToClose === null &&
        (!raw.conversionOfferToCloseRange || raw.conversionOfferToCloseRange === "not-known"),

      // Qualitative Metrics
      responseTime: mapResponseTime(raw.responseTime),
      leadResponseTimeUnknown: !raw.responseTime || raw.responseTime === "not-known",
      
      followUpDiscipline: mapFollowUpDiscipline(raw.followUpDiscipline),
      followUpDisciplineUnknown: !raw.followUpDiscipline || raw.followUpDiscipline === "not-known",
      
      pipelineTransparency: mapPipelineTransparency(raw.pipelineTransparency),
      pipelineTransparencyUnknown: !raw.pipelineTransparency || raw.pipelineTransparency === "not-known",
      
      leadQuality: mapLeadQuality(raw.leadQuality),
      leadQualityUnknown: !raw.leadQuality || raw.leadQuality === "not-known",
      
      // Company Context – teamSize aus productiveReps (einzige Quelle)
      teamSize: raw.productiveReps ?? null,
      teamSizeUnknown: raw.productiveReps === null,

      // NEW: Rep performance / target / pipeline layer (v2)
      productiveRepsExact: raw.productiveReps,
      productiveRepsUnknown: raw.productiveReps === null,

      avgRevenuePerRepExact:
        raw.avgRevenuePerRepVisibility === "not-known" ? null : raw.avgRevenuePerRep,
      avgRevenuePerRepUnknown:
        raw.avgRevenuePerRepVisibility === "not-known" || raw.avgRevenuePerRep === null,

      totalRevenueTargetYearExact: raw.totalRevenueTargetYear,
      totalRevenueTargetYearUnknown: raw.totalRevenueTargetYear === null,

      revenueAchievedYTDExact: raw.revenueAchievedYTD,
      revenueAchievedYTDUnknown: raw.revenueAchievedYTD === null,

      revenueTargetBrokenDownToReps: raw.revenueTargetBrokenDownToReps,
      revenueTargetBrokenDownToRepsUnknown:
        !raw.revenueTargetBrokenDownToReps || raw.revenueTargetBrokenDownToReps === "not-known",

      repsHaveRevenueTarget: raw.repsHaveRevenueTarget,
      repsHaveRevenueTargetUnknown:
        !raw.repsHaveRevenueTarget || raw.repsHaveRevenueTarget === "not-known",

      avgRevenueTargetPerRepExact: raw.avgRevenueTargetPerRep,
      avgRevenueTargetPerRepUnknown: raw.avgRevenueTargetPerRep === null,

      repsHitTargetShare: raw.repsOnTrackToTarget,
      repsHitTargetShareUnknown:
        !raw.repsOnTrackToTarget || raw.repsOnTrackToTarget === "not-known",

      repPerformanceVariance: raw.repPerformanceVariance,
      repPerformanceVarianceUnknown:
        !raw.repPerformanceVariance || raw.repPerformanceVariance === "not-known",

      pipelineCoverageReality: raw.repsWithEnoughPipeline,
      pipelineCoverageRealityUnknown:
        !raw.repsWithEnoughPipeline || raw.repsWithEnoughPipeline === "not-known",

      pipelineConfidence: raw.pipelineConfidence,
      pipelineConfidenceUnknown:
        !raw.pipelineConfidence || raw.pipelineConfidence === "not-known",

      repReviewCadence: raw.repReviewCadence,
      repReviewCadenceUnknown:
        !raw.repReviewCadence || raw.repReviewCadence === "not-known",

      // Legacy bridge
      pipelineCoverageVsTarget:
        raw.repsWithEnoughPipeline === "almost-all"
          ? "2x-3x"
          : raw.repsWithEnoughPipeline === "more-than-half"
          ? "1x-2x"
          : raw.repsWithEnoughPipeline === "less-than-half"
          ? "under-1x"
          : raw.repsWithEnoughPipeline === "hardly-any"
          ? "under-1x"
          : "not-known",

      // stalePipelineShare: Proxy-Mapping entfernt — exakter Wert über stalePipelineSharePercentExact

      realisticPipelineCloseRateExact:
        raw.pipelineConfidence === "very-high"
          ? 80
          : raw.pipelineConfidence === "rather-high"
          ? 65
          : raw.pipelineConfidence === "rather-low"
          ? 40
          : raw.pipelineConfidence === "very-low"
          ? 20
          : null,

      realisticPipelineCloseRateUnknown:
        !raw.pipelineConfidence || raw.pipelineConfidence === "not-known",

      // NEW: Step 2 Conversion extras
      daysFromDiscoveryToProposalExact: raw.daysFromDiscoveryToProposal,
      daysFromDiscoveryToProposalUnknown: raw.daysFromDiscoveryToProposal === null,

      proposalsWithNextStepPercentExact: raw.proposalsWithNextStepPercent,
      proposalsWithNextStepPercentUnknown: raw.proposalsWithNextStepPercent === null,

      // NEW: Step 3 Speed & Execution extras
      avgFollowUpsPerLeadExact: raw.avgFollowUpsPerLead,
      avgFollowUpsPerLeadUnknown: raw.avgFollowUpsPerLead === null,

      decisionMakerInFirstMeetingPercentExact: raw.decisionMakerInFirstMeetingPercent,
      decisionMakerInFirstMeetingPercentUnknown: raw.decisionMakerInFirstMeetingPercent === null,

      decisionMakerInProcess: raw.decisionMakerInProcess,
      decisionMakerInProcessUnknown:
        !raw.decisionMakerInProcess || raw.decisionMakerInProcess === "not-known",

      discoveryNoShowRatePercentExact: raw.discoveryNoShowRatePercent,
      discoveryNoShowRatePercentUnknown: raw.discoveryNoShowRatePercent === null,

      discoveryFrameworkStep3: raw.discoveryFrameworkStep3,
      discoveryFrameworkStep3Unknown:
        !raw.discoveryFrameworkStep3 || raw.discoveryFrameworkStep3 === "not-known",

      // Tools & Systems: salesPlaybookAvailable (ersetzt discoveryFramework)
      salesPlaybookAvailable: raw.salesPlaybookAvailable,
      salesPlaybookAvailableUnknown:
        !raw.salesPlaybookAvailable || raw.salesPlaybookAvailable === "not-known",

      // NEW: Step 4 Pipeline extras
      stalePipelineSharePercentExact: raw.stalePipelineSharePercent,
      stalePipelineSharePercentUnknown: raw.stalePipelineSharePercent === null,

      forecastCoverageRatioExact: raw.forecastCoverageRatio,
      forecastCoverageRatioUnknown: raw.forecastCoverageRatio === null,

      // NEW: Step 5 Lead Quality extras
      leadScoringMaturity: raw.leadScoringMaturity,
      leadScoringMaturityUnknown:
        !raw.leadScoringMaturity || raw.leadScoringMaturity === "not-known",

      qualifiedLeadRatePercentExact: raw.qualifiedLeadRatePercent,
      qualifiedLeadRatePercentUnknown: raw.qualifiedLeadRatePercent === null,

      // NEW: ICP Clarity & Market Presence (Step 4)
      icpClarity: raw.icpClarity,
      icpClarityUnknown: !raw.icpClarity || raw.icpClarity === "not-known",

      marketPresence: raw.marketPresence,
      marketPresenceUnknown: !raw.marketPresence || raw.marketPresence === "not-known",
    };


    setAnswers(rawAnswersData);

    // Store dynamic signals
    setDynamicSignals(normalized.dynamicSignals);

    // Calculate result (this sets the result in the store)
    calculateResult();

    // ── Admin-Submission speichern (Supabase + localStorage-Fallback) ──
    try {
      const companyProfileForAdmin = {
        companyName: normalized.companyContext.companyName,
        companyDomain: raw.companyDomain || '',
        industry: normalized.companyContext.industry,
        businessModel: normalized.companyContext.businessModel as 'B2B' | 'B2C' | 'Both',
        revenue: '',
        employees: '',
        teamSize: String(raw.productiveReps ?? ''),
        averageDealValue: '',
        salesCycle: '',
        hasCRM: raw.crmAvailable === 'yes',
        crmName: raw.crmName || undefined,
        hasMarketingAutomation: raw.marketingAutomation === 'yes',
        leadSources: [] as string[],
        contactFirstName: raw.contactFirstName || '',
        contactLastName: raw.contactLastName || '',
        contactPosition: raw.contactRole || '',
        contactEmail: raw.contactEmail || '',
        contactPhone: raw.contactPhone || undefined,
        funnelData: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          averageDealSize: raw.averageDealSize ?? null,
          averageDealSizeUnknown: raw["averageDealSize_unknown"] ?? false,
          salesCycleMonths: raw.salesCycleMonths ?? null,
          salesCycleMonthsUnknown: raw["salesCycleMonths_unknown"] ?? false,
          monthlyDeals: raw.monthlyDeals ?? null,
          monthlyDealsUnknown: raw["monthlyDeals_unknown"] ?? false,
          monthlyContacts: raw.monthlyContacts ?? null,
          monthlyContactsUnknown: raw["monthlyContacts_unknown"] ?? false,
          monthlyDiscoveryCalls: raw.monthlyDiscoveryCalls ?? null,
          monthlyDiscoveryCallsUnknown: raw["monthlyDiscoveryCalls_unknown"] ?? false,
          monthlyPresentations: raw.monthlyPresentations ?? null,
          monthlyPresentationsUnknown: raw["monthlyPresentations_unknown"] ?? false,
          monthlyRevenue: (raw.monthlyDeals ?? 0) * (raw.averageDealSize ?? 0) || null,
          monthlyRevenueUnknown: raw["monthlyRevenueOverride_unknown"] ?? false,
          responseTime: raw.responseTime ?? null,
          responseTimeUnknown: raw["responseTime_unknown"] ?? false,
          followUpDiscipline: raw.followUpDiscipline ?? null,
          followUpDisciplineUnknown: raw["followUpDiscipline_unknown"] ?? false,
          leadQuality: raw.leadQuality ?? null,
          leadQualityUnknown: raw["leadQuality_unknown"] ?? false,
          pipelineTransparency: raw.pipelineTransparency ?? null,
          pipelineTransparencyUnknown: raw["pipelineTransparency_unknown"] ?? false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        acquisitionMethods: {
          inbound: Number(raw.inbound ?? 0),
          outbound: Number(raw.outbound ?? 0),
          partners: Number(raw.partners ?? 0),
          existing: Number(raw.existing ?? 0),
          referrals: Number(raw.referrals ?? 0),
          events: Number(raw.events ?? 0),
        },
      };
      const resultForAdmin = calculateAuditResult(rawAnswersData, companyProfileForAdmin);
      const submissionId = `audit-submission-${Date.now()}`;
      const submission = {
        id: submissionId,
        timestamp: new Date().toISOString(),
        companyProfile: companyProfileForAdmin,
        result: resultForAdmin,
        answers: rawAnswersData,
      };

      // ── Primär: Supabase (zentrale Datenbank für Admin-Dashboard) ──
      const supabaseRow = {
        id: submissionId,
        company_name: companyProfileForAdmin.companyName || null,
        company_domain: companyProfileForAdmin.companyDomain || null,
        contact_first_name: companyProfileForAdmin.contactFirstName || null,
        contact_last_name: companyProfileForAdmin.contactLastName || null,
        contact_email: companyProfileForAdmin.contactEmail || null,
        contact_phone: companyProfileForAdmin.contactPhone || null,
        contact_position: companyProfileForAdmin.contactPosition || null,
        industry: companyProfileForAdmin.industry || null,
        business_model: companyProfileForAdmin.businessModel || null,
        score: (resultForAdmin as Record<string, unknown>)?.['score'] as number ?? null,
        potential_revenue: (resultForAdmin as Record<string, unknown>)?.['potentialRevenue'] as number ?? null,
        monthly_deals: raw.monthlyDeals ?? null,
        average_deal_size: raw.averageDealSize ?? null,
        sales_cycle_months: raw.salesCycleMonths ?? null,
        monthly_contacts: raw.monthlyContacts ?? null,
        monthly_revenue: (raw.monthlyDeals ?? 0) * (raw.averageDealSize ?? 0) || null,
        data_completeness: (resultForAdmin as Record<string, unknown>)?.['dataCompleteness'] as number ?? null,
        unknown_field_count: (resultForAdmin as Record<string, unknown>)?.['unknownFieldCount'] as number ?? null,
        raw_form: raw as unknown as Record<string, unknown>,
        result_json: resultForAdmin as unknown as Record<string, unknown>,
      };
      const { error: sbError } = await supabase
        .from('audit_submissions_2026_06_10')
        .insert(supabaseRow);
      if (sbError) {
        // Fallback: localStorage wenn Supabase fehlschlägt
        localStorage.setItem(submissionId, JSON.stringify(submission));
      }
    } catch (err) {
      console.error('Admin-Submission konnte nicht gespeichert werden:', err);
    }

    // Navigate to results master
    navigate(ROUTE_PATHS.RESULTS_MASTER);
  }

  return (
    <Layout>
      <SalesAuditWizardMaster
        initialData={initialData}
        onSubmit={handleSubmit}
        onChange={handleChange}
        submitLabel="Audit auswerten"
      />
    </Layout>
  );
}

// -------------------------------------------------------
// MAPPING HELPERS
// -------------------------------------------------------

/**
 * Converts a ConversionRangeOption string to its midpoint numeric value.
 * Returns null if the range is "not-known" or unrecognized.
 */
function convRangeToMidpoint(range: string | null | undefined): number | null {
  if (!range || range === "not-known") return null;
  const map: Record<string, number> = {
    "under-10":  5,
    "10-20":    15,
    "20-30":    25,
    "30-50":    40,
    "50+":      60,
  };
  return map[range] ?? null;
}

function mapBusinessModel(value: string): "B2B" | "B2C" | "Both" {
  if (value === "b2b-services" || value === "b2b-saas" || value === "b2b-industrial") return "B2B";
  if (value === "ecommerce") return "B2C";
  return "Both";
}

function mapResponseTime(value: string): "under-5min" | "under-1h" | "same-day" | "1-3days" | "over-3days" {
  if (!value || value === "not-known") return "1-3days"; // conservative fallback when unknown
  const map: Record<string, "under-5min" | "under-1h" | "same-day" | "1-3days" | "over-3days"> = {
    "under-1h": "under-1h",
    "1-3h": "under-1h",
    "same-day": "same-day",
    "1-3days": "1-3days",
    "more-than-3days": "over-3days",
  };
  return map[value] ?? "1-3days";
}

function mapFollowUpDiscipline(value: string): "very-structured" | "partly-structured" | "irregular" | "rarely" {
  const map: Record<string, "very-structured" | "partly-structured" | "irregular" | "rarely"> = {
    "very-structured": "very-structured",
    "partly-structured": "partly-structured",
    "ad-hoc": "irregular",
    "hardly-any": "rarely",
  };
  return map[value] || "irregular";
}

function mapLeadQuality(value: string): "very-high" | "good" | "mixed" | "weak" {
  const map: Record<string, "very-high" | "good" | "mixed" | "weak"> = {
    "very-high": "very-high",
    good: "good",
    mixed: "mixed",
    weak: "weak",
  };
  return map[value] || "mixed";
}

function mapPipelineTransparency(value: string): "full" | "partial" | "low" | "minimal" {
  const map: Record<string, "full" | "partial" | "low" | "minimal"> = {
    full: "full",
    partial: "partial",
    low: "low",
    minimal: "minimal",
  };
  return map[value] || "partial";
}